import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Space, Tag, Typography } from 'antd';
import { PhoneOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useIncomingCallStore } from '../store/incomingCallStore';
import { useAuth } from '@features/auth/hooks/useAuth';
import './IncomingCallNotification.css';

const { Text, Title } = Typography;

export function IncomingCallNotification() {
  const { t } = useTranslation(['customers', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { activeNotification, removeNotification, setActiveNotification, notifications } =
    useIncomingCallStore();
  const [isVisible, setIsVisible] = useState(true);

  if (!activeNotification || !isVisible) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    removeNotification(activeNotification.id);
    
    // Показываем следующее уведомление, если есть
    const nextNotification = notifications.find((n) => n.id !== activeNotification.id);
    if (nextNotification) {
      setActiveNotification(nextNotification);
      setIsVisible(true);
    } else {
      setActiveNotification(null);
    }
  };

  const handleViewCustomer = () => {
    if (activeNotification.customerId && activeNotification.customerId > 0) {
      navigate(`/customers/${activeNotification.customerId}`);
      handleClose();
    }
  };

  const handleAddPotentialCustomer = () => {
    navigate(`/customers/new?is_potential=1&phone=${encodeURIComponent(activeNotification.phoneNumber)}`);
    handleClose();
  };

  const canAddPotentialCustomer = hasPermission('customers.potential.write');

  // Парсим HTML сообщение и извлекаем информацию
  const parseHtmlMessage = (html: string) => {
    // Упрощенный парсинг HTML (можно улучшить)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Извлекаем номер телефона
    const phoneMatch = html.match(/(\+?[\d\s\-()]+)/);
    const phoneNumber = phoneMatch ? phoneMatch[1].trim() : activeNotification.phoneNumber;
    
    // Извлекаем информацию об абоненте из HTML
    const links = tempDiv.querySelectorAll('a');
    const customerLinks = Array.from(links).filter((link) =>
      link.href.includes('/customers/'),
    );
    
    return {
      phoneNumber,
      customerLinks,
      htmlContent: html,
    };
  };

  const parsed = parseHtmlMessage(activeNotification.htmlMessage);
  const hasCustomer = activeNotification.customerId !== null && activeNotification.customerId > 0;
  const hasMultipleCustomers = activeNotification.customerId !== null && activeNotification.customerId < 0;

  return (
    <Card
      className="incoming-call-notification"
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 1000,
        minWidth: 350,
        maxWidth: 500,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={handleClose}
          aria-label="Закрыть"
        />
      }
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PhoneOutlined style={{ color: '#1890ff' }} />{t('customers:incomingCall.title')}</Title>
        </div>

        <div>
          <Text strong style={{ fontSize: '16px' }}>
            {parsed.phoneNumber}
          </Text>
          {activeNotification.callerIdName && (
            <div>
              <Text type="secondary">{activeNotification.callerIdName}</Text>
            </div>
          )}
        </div>

        {/* Информация об абоненте */}
        {hasCustomer && (
          <div>
            <Tag color="success">{t('customers:incomingCall.found')}</Tag>
            {hasMultipleCustomers && (
              <Tag color="warning">
                Найдено {Math.abs(activeNotification.customerId || 0)} абонентов
              </Tag>
            )}
            <Button
              type="link"
              onClick={handleViewCustomer}
              style={{ padding: 0, marginTop: 8 }}
            >{t('customers:incomingCall.openCard')}</Button>
          </div>
        )}

        {/* HTML сообщение (безопасно отображаем) */}
        <div
          className="incoming-call-notification-content"
          dangerouslySetInnerHTML={{ __html: parsed.htmlContent }}
          style={{
            fontSize: '13px',
            lineHeight: '1.5',
          }}
        />

        {/* Действия для неизвестного абонента */}
        {!hasCustomer && (
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            {canAddPotentialCustomer && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddPotentialCustomer}
                block
              >{t('customers:incomingCall.addPotential')}</Button>
            )}
            {/* Кнопки «Добавить телефон к существующему абоненту» здесь БОЛЬШЕ НЕТ.
                Её обработчик только писал номер звонящего в консоль браузера
                (`// TODO: Открыть модалку…`) — то есть контрол обещал действие, не делал
                ничего и заодно выносил PII в консоль. Тот же приём, что со снятой областью
                поиска «Адрес»: не предлагать оператору то, чего нет. Функция записана
                долгом в TODOS.md — вернуть вместе с реализацией, не раньше. */}
          </Space>
        )}
      </Space>
    </Card>
  );
}
